<template>
    <div class="sign-in-container" v-loading="loading">
        <template v-if="error">
            <div class="error-message">
                <el-alert :title="error" type="error" :closable="false" show-icon />
                <el-button class="retry-button" type="primary" @click="fetchCourse">
                    Retry
                </el-button>
            </div>
        </template>

        <template v-else-if="course">
            <div class="course-details">
                <h2>{{ course.name }}</h2>
                <div class="info-row">
                    <span class="label">Category:</span>
                    <span>{{ course.category?.name || 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="label">Semester:</span>
                    <span>{{ course.semester?.name || 'N/A' }}</span>
                </div>
                <div class="info-row">
                    <span class="label">Students:
                        <el-tooltip
                            content="Status is the status of the course. It can be available, almost full, or full."
                            placement="top" effect="light">
                            <el-icon class="el-icon-question"
                                style="margin-left: 5px; top: 4px; color: yellowgreen; cursor: help;">
                                <QuestionFilled />
                            </el-icon>
                        </el-tooltip>
                    </span>
                    <el-tag class="my-tag" :type="getStatusType(course)">
                        {{ getStatusText(course) }}
                    </el-tag>
                    <span v-if="course.needLottery" style="color: orange; margin-right: 5px;">(draw to keep)</span>
                    <span>{{ course.maxStudent }}</span>
                </div>
                <div class="info-row">
                    <span class="label">Price: 
                        <el-tooltip
                            content="price = tuition + insurance + material + other."
                            placement="top" effect="light">
                            <el-icon class="el-icon-question"
                                style="margin-left: 5px; top: 4px; color: yellowgreen; cursor: help;">
                                <QuestionFilled />
                            </el-icon>
                        </el-tooltip>
                    </span>
                    <span> {{ course.tuition }} + {{ course.insurance }} + {{ course.material }} + {{ course.other }} = {{ course.tuition + course.insurance + course.material + course.other }} </span>
                </div>
                <div class="info-row" v-if="course.needLottery || course.needInterview">
                    <span class="label">Options:</span>
                    <el-tag v-if="course.needLottery" type="success" class="my-tag">Lottery</el-tag>
                    <el-tag v-if="course.needInterview" type="warning" class="my-tag">Interview</el-tag>
                </div>
                <div class="schedules">
                    <label>Class Schedule ({{ course.schedules.length }} lessons)</label>
                    <el-table :data="course.schedules" style="width: 100%" class="schedules-table">
                        <el-table-column prop="startDate" label="Time">
                            <template #default="scope">
                                {{ new Date(scope.row.startDate).toLocaleString([], {
                                year: 'numeric', month: '2-digit', day: '2-digit',
                                weekday: 'long',
                                hour: '2-digit', minute: '2-digit'}) }}
                                -
                                {{ new Date(scope.row.endDate).toLocaleString([], {
                                hour: '2-digit', minute: '2-digit'}) }}
                            </template>
                        </el-table-column>
                        <el-table-column prop="room.name" label="Room" />
                        <el-table-column label="Teachers">
                            <template #default="scope">
                                {{scope.row.teachers.map(t => t.fullName).join(', ')}}
                            </template>
                        </el-table-column>
                    </el-table>
                    <!-- <el-timeline>
                            <el-timeline-item
                                v-for="schedule in course.schedules"
                                :key="schedule.id"
                                :timestamp="formatDate(schedule.startDate)"
                                placement="top"
                            >
                                <el-card class="schedule-card">
                                    <div class="schedule-time">
                                        {{ formatTimeRange(schedule.startDate, schedule.endDate) }}
                                    </div>
                                    <div v-if="schedule.room" class="schedule-room">
                                        <i class="el-icon-location"></i>
                                        Room: {{ schedule.room.name }}
                                    </div>
                                    <div class="schedule-teachers">
                                        <i class="el-icon-user"></i>
                                        Teachers: 
                                        <span v-for="(teacher, index) in schedule.teachers" :key="teacher.id">
                                            {{ teacher.fullName }}{{ index < schedule.teachers.length - 1 ? ', ' : '' }}
                                        </span>
                                    </div>
                                </el-card>
                            </el-timeline-item>
                        </el-timeline> -->
                </div>

                <div class="action-buttons">
                    <el-button type="primary" @click="nextOperation" :loading="operationLoading"
                        :disabled="!nextOperation"
                        :style="!nextOperation ? 'background-color: #ccc; color: #fff;' : ''">
                        {{ nextOperationName }}
                    </el-button>
                    <el-button @click="goBack">Back</el-button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import { BACKEND_URL } from '../../../share-source/src/config/config.js';
import { ElMessage } from 'element-plus';
import { useUserStore } from '../stores/Global.js';
import { SignupStatus } from '../constants/SignupStatus.js';
import { QuestionFilled } from '@element-plus/icons-vue';
import { CourseStatus } from '@/constants/CourseStatus.js';

export default {
    setup() {
        const userStore = useUserStore();
        return {
            userStore
        }
    },
    components: {
        QuestionFilled
    },  
    name: 'SignupView',
    data() {
        return {
            course: null,
            loading: false,
            error: null,
            operationLoading: false,
            studentId: null,
            signup: null,
            nextOperation: null,
            nextOperationName: "error"
        }
    },
    computed: {
    },
    async mounted() {
        this.studentId = this.userStore.getStudentId();
        if (!this.studentId) {
            ElMessage.error('Please choose a student first');
            this.$router.push('/home');
            return;
        }
        await this.fetchCourse();
        this.fetchSignup();
    },
    methods: {
        async doSignup() {
            try {
                this.operationLoading = true;
                const response = await axios.post(`${BACKEND_URL}/api/signup/signup/${this.course.id}/${this.studentId}`);
                this.signup = response.data;
                if (this.signup.status === "PAYABLE") {
                    this.nextOperation = this.pay;
                    this.nextOperationName = 'Pay';
                } else if (this.signup.status === "SIGNED_UP") {
                    this.nextOperation = null;
                    this.nextOperationName = 'Signed Up';
                }
                ElMessage.success('Successfully signed up for the course');
                // this.$router.push('/my-course');
            } catch (error) {
                ElMessage.error(error.response?.data?.message || 'Failed to sign up');
            } finally {
                this.operationLoading = false;
            }
        },
        pay() {
            this.operationLoading = true;
            axios.post(`${BACKEND_URL}/api/signup/pay/${this.signup.id}`).then(response => {
                // Mock payment password input dialog
                let payment = response.data;
                return this.$confirm('Please input payment password(123456):', 'Payment id: ' + payment.id, {
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                    inputType: 'password',
                    showInput: true
                }).then(({ value }) => {
                    // Mock password validation
                    if (value === '123456') { // Mock correct password
                        // Call payment complete API
                        return axios.post(`${BACKEND_URL}/api/payment/completed/${payment.id}`);
                    } else {
                        return Promise.reject(new Error('Invalid payment password'));
                    }
                }).then(() => {
                    this.$message.success('Payment verified successfully');
                    this.signup.status = SignupStatus.PAID;
                    this.nextOperation = this.refund;
                    this.nextOperationName = 'Refund';
                });
            }).catch(err => {
                if (err === 'cancel') {
                    this.$message.info('Payment cancelled');
                } else {
                    this.$message.error(err.message || 'Payment verification failed');
                }
            }).finally(() => {
                this.operationLoading = false;
            });
        },
        refund() {
            this.operationLoading = true;
            axios.post(`${BACKEND_URL}/api/signup/refunding/${this.signup.id}`).then(response => {
                console.log('refunding signup:', response.data);
                this.$message.success('Refund successfully');
                this.signup.status = SignupStatus.REFUNDING;
                this.nextOperation = null;
                this.nextOperationName = 'Refunding';
            }).catch(error => {
                this.$message.error(error.response?.data?.message || 'Failed to refund');
            }).finally(() => {
                this.operationLoading = false;
            });
        },
        handleSignedUp() {
            this.nextOperation = null;
            this.nextOperationName = 'Signed Up';

            // Check lottery status
            if (this.signup.lotteryStatus === -1) {
                this.nextOperationName = 'Failed Lottery';
            } else if (this.signup.lotteryStatus === 1) {
                this.nextOperationName = 'Lottery Winner';
            }

            // Check interview status
            if (this.signup.interviewStatus === -1) {
                this.nextOperationName = 'Failed Interview';
            } else if (this.signup.interviewStatus === 1) {
                this.nextOperationName = 'Interview Passed';
            }

            // the backend should transfer status to payable.

            // If lottery passes or not needed, and interview passes or not needed, enable pay
            // const lotteryPassed = this.signup.lotteryStatus === 1 || !this.course.needLottery;
            // const interviewPassed = this.signup.interviewStatus === 1 || !this.course.needInterview;

            // if (lotteryPassed && interviewPassed) {
            //     this.nextOperation = this.pay;
            //     this.nextOperationName = 'Pay';
            // }
        },
        handleCreated() {
            if (this.isFull(this.course)) {
                this.nextOperation = null;
                this.nextOperationName = 'Full';
            } else if (this.course.phase != CourseStatus.OPEN) {
                this.nextOperation = null;
                this.nextOperationName = 'NOT OPEN';
            } else {
                this.nextOperation = this.doSignup;
                this.nextOperationName = 'Sign Up';
            }
        },
        handleNotFound() {
            if (this.isFull(this.course)) {
                this.nextOperation = null;
                this.nextOperationName = 'Full';
            } else if (this.course.phase != CourseStatus.OPEN) {
                this.nextOperation = null;
                this.nextOperationName = 'NOT OPEN';
            } else {
                this.nextOperation = this.doSignup;
                this.nextOperationName = 'Sign Up';
            }
        },
        handlePaid() {
            this.nextOperation = this.refund;
            this.nextOperationName = 'Refund';
        },
        handleRefunding() {
            this.nextOperation = null;
            this.nextOperationName = 'Refunding';
        },
        handleRefunded() {
            this.nextOperation = null;
            this.nextOperationName = 'Refunded';
        },
        handleTransferred() {
            this.nextOperation = null;
            this.nextOperationName = 'Transferred';
        },
        handleCancelled() {
            this.nextOperation = null;
            this.nextOperationName = 'Cancelled';
        },
        handleExpired() {
            this.nextOperation = null;
            this.nextOperationName = 'Expired';
        },
        handlePayable() {
            this.nextOperation = this.pay;
            this.nextOperationName = 'Pay';
        },
        handleFailedLottery() {
            this.nextOperation = null;
            this.nextOperationName = 'Failed Lottery';
        },
        handleFailedInterview() {
            this.nextOperation = null;
            this.nextOperationName = 'Failed Interview';
        },
        async fetchSignup() {
            if (!this.studentId) return;
            try {
                let response;
                try {
                    if (this.$route.params.courseId) {
                        response = await axios.get(
                            `${BACKEND_URL}/api/signup/get_by_course_and_student/${this.$route.params.courseId}/${this.studentId}`
                        );
                    } else if (this.$route.params.signupId) {
                        response = await axios.get(
                            `${BACKEND_URL}/api/signup/get_by_id/${this.$route.params.signupId}`
                        );
                    }
                } catch (err) {
                    console.error('Error fetching signup:', err);
                    ElMessage({
                        message: err.response?.data?.message || 'Failed to fetch signup',
                        type: 'error',
                        duration: 0,
                        showClose: true
                    });
                    return;
                }
                
                this.signup = response.data;
                console.log('signup:', this.signup);

                // the backend had translated the status to string
                if (!this.signup) {
                    this.handleNotFound();
                } else if (this.signup.status === "CREATED") {
                    this.handleCreated(); 
                } else if (this.signup.status === "EXPIRED") {
                    this.handleExpired();
                } else if (this.signup.status === "CANCELLED") {
                    this.handleCancelled();
                } else if (this.signup.status === "FAILED_LOTTERY") {
                    this.handleFailedLottery();
                } else if (this.signup.status === "FAILED_INTERVIEW") {
                    this.handleFailedInterview();
                } else if (this.signup.status === "SIGNED_UP") {
                    this.handleSignedUp();
                } else if (this.signup.status === "PAYABLE") {
                    this.handlePayable();
                } else if (this.signup.status === "PAID") {
                    this.handlePaid();
                } else if (this.signup.status === "TRANSFERRED") {
                    this.handleTransferred();
                } else if (this.signup.status === "REFUNDING") {
                    this.handleRefunding();
                } else if (this.signup.status === "REFUNDED") {
                    this.handleRefunded();
                } else {
                    // TODO: handle other status
                    console.log('Unhandled status:', this.signup.status);
                }
            } catch (error) {
                console.error('Error checking signup status:', error);
                if (error.response) {
                    console.error('Response data:', error.response.data);
                    console.error('Response status:', error.response.status);
                }
            }
        },  
        async fetchCourse() {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await axios.get(
                    `${BACKEND_URL}/api/course/get/${this.$route.params.courseId}`
                );
                this.course = response.data;
            } catch (error) {
                this.error = error.response?.data?.message || 'Failed to fetch course details';
                console.error('Error fetching course:', error);
            } finally {
                this.loading = false;
            }
        },
        
        goBack() {
            this.$router.go(-1);
        },

        formatDate(date) {
            return new Date(date).toLocaleDateString();
        },

        formatTimeRange(start, end) {
            const startTime = new Date(start).toLocaleTimeString();
            const endTime = new Date(end).toLocaleTimeString();
            return `${startTime} - ${endTime}`;
        },

        getStatusType(course) {
            if (course.currentStudent >= course.maxStudent) return 'danger';
            if (course.currentStudent >= course.maxStudent * 0.8) return 'warning';
            return 'success';
        },

        isFull(course) {
            if (course.needLottery) {
                return false;
            } else {
                return course.currentStudent >= course.maxStudent;
            }
        },

        isAlmostFull(course) {
            return course.currentStudent >= course.maxStudent * 0.8;
        },  

        getStatusText(course) {
            console.log('course:', course);
            if (course.needLottery) {
                return 'Available';
            } else {
                console.log('course.currentStudent:', course.currentStudent);
                console.log('course.maxStudent:', course.maxStudent);
                if (course.currentStudent >= course.maxStudent) return 'Full';
                if (course.currentStudent >= course.maxStudent * 0.8) return 'Almost Full';
                return 'Available';
            }
        }
    }
}
</script>

<style scoped>
.my-tag {
    margin-right: 10px;
    /* float: right; */
}   

.sign-in-container {
    /* max-width: 800px; */
    /* margin: 20px auto; */
    /* padding: 0 20px; */
    display: flex;
    flex-direction: column;
    /* margin: 8px; */
    /* justify-content: center; */
    /* align-items: center; */
}

.course-details {
    padding: 2px;
    display: flex;
    flex-direction: column;
    flex: 1;
    /* don't know why this is needed */
    overflow-y: hidden;
}

.info-row {
    margin: 6px 0;
    display: flex;
    align-items: center;
}

.label {
    font-weight: bold;
    margin-right: 4px;
    min-width: 100px;
}

.schedule-card {
    margin-bottom: 10px;
}

.schedule-time {
    font-weight: bold;
    margin-bottom: 8px;
}

.schedule-room, .schedule-teachers {
    margin: 5px 0;
    color: #606266;
}

.schedule-room i, .schedule-teachers i {
    margin-right: 5px;
}

.action-buttons {
    margin: 10px;
    display: flex;
    gap: 15px;
    justify-content: center;
}

.error-message {
    text-align: center;
    padding: 20px;
}

.retry-button {
    margin-top: 15px;
}

:deep(.el-timeline-item__timestamp) {
    font-size: 14px;
    color: #606266;
}

.schedules {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: hidden;
}

</style>
